import { NextPage } from 'next'
import { useTranslation } from '../../utils/convergence'
import React, { ReactElement, useEffect, useState } from 'react'
import { Head, Verify } from '../../../src/component'
import { Route, usePostRegistrationRoute, useRoutePageName } from '../../../src/config/router'
import { TrackOnMount, TrackPageViewed } from '../../../src/integrations/monitoring'
import { VerificationErrorCode, VerificationFlowRoute } from '../../../src/model'
import { TrackingEvent } from '../../../src/model/monitoring'
import {
  captureCashConsent,
  setCheckoutError,
  setDisplayProcessingModal,
  setIsMobileLogin,
  useAppDispatch,
  useAppRouter,
  useAutoRememberMeStepUpReason,
  useConsumerFeatureFlagReady,
  useIsAutoRememberMeStepUp,
  useVerificationFlow,
} from '../../../src/state'
import { CashSignInTerms } from '../../../src/component/CashSignInTerms'
import { getBackRoute, getCheckoutError } from './helper'

export const VerifyPage: NextPage = (): ReactElement => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const router = useAppRouter()
  const [disabled, setDisabled] = useState(false)
  const [pendingPostRegistrationRoute, setPendingPostRegistrationRoute] = useState<VerificationFlowRoute | undefined>()
  const { flow, showCashTerms } = useVerificationFlow()
  const pageName = useRoutePageName()
  const hasFeatureFlagsReady = useConsumerFeatureFlagReady()
  const postRegistrationRoute = usePostRegistrationRoute()
  const isAutoRememberMeStepUp = useIsAutoRememberMeStepUp()
  const stepUpReason = useAutoRememberMeStepUpReason()

  const onSuccess = (result?: VerificationFlowRoute): void => {
    if (showCashTerms) {
      dispatch(captureCashConsent())
    }
    if (result?.triggerProcessingModal === true) {
      dispatch(setDisplayProcessingModal(true))
      return
    }

    if (result?.route !== undefined) {
      router.push(result.route, result.locale)
      return
    }

    if (result?.deferPostRegistrationRouteUntilFeatureFlagsReady === true) {
      setPendingPostRegistrationRoute(result)
      return
    }

    router.push(Route.TERMINAL_ERROR)
  }

  const resetDisabledState = (): void => {
    setDisabled(false)
  }

  const onTerminalError = (e?: VerificationErrorCode): void => {
    if (e === undefined) {
      return router.push(Route.TERMINAL_ERROR)
    }

    dispatch(setCheckoutError(getCheckoutError(e)))
    router.push(Route.TERMINAL_ERROR)
  }

  const onComplete = (): void => {
    setDisabled(true)
  }

  const onError = (): void => {
    dispatch(setIsMobileLogin(false))
    const route = getBackRoute(flow.context)
    router.push(route)
  }

  const pageTitle = `${pageName} | ${t('common:brandName')}`

  useEffect(() => {
    if (pendingPostRegistrationRoute?.deferPostRegistrationRouteUntilFeatureFlagsReady !== true || !hasFeatureFlagsReady) {
      return
    }

    setPendingPostRegistrationRoute(undefined)
    router.push(postRegistrationRoute, pendingPostRegistrationRoute.locale)
  }, [hasFeatureFlagsReady, pendingPostRegistrationRoute, postRegistrationRoute, router])

  useEffect(() => {
    router.prefetch(Route.TERMINAL_ERROR)

    flow.prefetchRoutes.forEach((route): void => {
      router.prefetch(route)
    })
  }, [])

  return (
    <>
      <Head title={pageTitle} />
      <Verify
        onSuccess={onSuccess}
        resetDisabledState={resetDisabledState}
        onTerminalError={onTerminalError}
        onComplete={onComplete}
        disabled={disabled}
        flow={flow}
        onError={onError}
      />
      {showCashTerms && <CashSignInTerms />}
      <TrackPageViewed eventProperties={flow.enabled} />
      {isAutoRememberMeStepUp && <TrackOnMount eventName={TrackingEvent.TRIGGERED_AUTO_REMEMBER_ME_STEP_UP} eventProperties={{ pageName, stepUpReason }} />}
    </>
  )
}
